import type {
  ContextMenuType,
  NeedIncludeHidden,
  TabElementProperty,
  TabInfo,
  WorkArea,
  WorkAreaId,
} from '@core/core/types'
import type { TabType } from '@library/model'
import { isDefined } from 'common/helpers'
import type {
  BaseObject,
  CommonDomainType,
  DomainType,
  ProjectElementId,
  ProjectElementInfo,
  ProjectId,
  TabElementInfo,
  TabElementStateType,
  TabId,
} from 'common/types'

import { constructClientDispenser } from '../clientDispenserFactory'
import { axiosInstance } from '../common'
import { convertToApiElementType } from '../converters'
import { CollectionClient } from './__generated__/Collection'
import { DeleteElementClient } from './__generated__/DeleteElement'
import { DomainSwitchClient } from './__generated__/DomainSwitch'
import { ElementSettingClient } from './__generated__/ElementSetting'
import { HttpClient } from './__generated__/http-client'
import { ProjectElementClient } from './__generated__/ProjectElement'
import { StateClient } from './__generated__/State'
import { TabClient } from './__generated__/Tab'
import { TabElementClient } from './__generated__/TabElement'
import { WorkAreaClient } from './__generated__/WorkArea'
import {
  convertContextMenu,
  convertFromBaseObject,
  convertTabInfo,
  convertToApiProjectElementInfo,
  convertToBaseObject,
  convertToProjectElementInfo,
  convertToTabElementInfo,
  convertToTabElementProperties,
  convertToWorkArea,
} from './converters'

const Clients = {
  CollectionClient,
  ElementSettingClient,
  ProjectElementClient,
  StateClient,
  TabClient,
  TabElementClient,
  WorkAreaClient,
  DomainSwitchClient,
  DeleteElementClient,
} as const

const getClient = constructClientDispenser(Clients, HttpClient, axiosInstance)

/// CollectionClient

export const getTabElementPropertiesApi = (): Promise<TabElementProperty[]> => {
  const client = getClient('CollectionClient')
  const elementTypes = client.collectionGetElementTypeCollection()
  const elementTabTypes = client.collectionGetTabElementTypeCollection()
  return Promise.all([elementTypes, elementTabTypes]).then(([elementType, tabType]) =>
    convertToTabElementProperties(elementType, tabType),
  )
}

export const getContextMenuCollectionApi = (): Promise<ContextMenuType[]> => {
  const client = getClient('CollectionClient')
  return client.collectionGetContextMenuCollection().then(convertContextMenu)
}

export const getValidVelocityElementTypesApi = (): Promise<BaseObject[]> => {
  const client = getClient('CollectionClient')
  const validVelocityElementTypes = client.collectionGetValidVelocityElementTypes()
  return validVelocityElementTypes.then(types => types.map(convertToBaseObject))
}

/// DomainSwitchClient

export const setWindowDomainByTabIdApi = (
  projectId: ProjectId,
  tabId: TabId,
  domainType: CommonDomainType,
): Promise<void> => {
  const client = getClient('DomainSwitchClient')
  return client.domainSwitchSwitchDomain(projectId, tabId, { domainType })
}

export const getDomainElementsWithSelectabilityApi = (
  projectId: ProjectId,
  tabId: TabId,
): Promise<void> => {
  const client = getClient('DomainSwitchClient')
  return client.domainSwitchGetDomainElementsWithSelectability(projectId, tabId)
}

export const getAddedElementsSelectabilityApi = (
  projectId: ProjectId,
  tabId: TabId,
  elements: ProjectElementInfo[],
): Promise<void> => {
  const convertedElements = elements.map(convertToApiProjectElementInfo)
  const client = getClient('DomainSwitchClient')
  return client.domainSwitchGetDomainElementsWithSelectabilityByList(
    projectId,
    tabId,
    convertedElements,
  )
}

/// ElementSettingClient
/// ProjectElementClient

export const getTopLevelElementsByProjectIdApi = (
  projectId: ProjectId,
): Promise<ProjectElementInfo[]> => {
  const client = getClient('ProjectElementClient')
  return client
    .projectElementGetTopLevelElementsByProjectId(projectId)
    .then(projectInfos => projectInfos.map(convertToProjectElementInfo))
}

export const getElementsByParentIdApi = (
  projectId: ProjectId,
  parentId: string,
  tabId?: TabId,
): Promise<ProjectElementInfo[]> => {
  const client = getClient('ProjectElementClient')
  return client
    .projectElementGetElementsByParentId(projectId, parentId, { tabId })
    .then(projectInfos => projectInfos.map(convertToProjectElementInfo))
}

export const getElementByProjectElementIdApi = (
  projectId: ProjectId,
  projectEntityId: string,
  elementType: BaseObject,
): Promise<ProjectElementInfo> => {
  const client = getClient('ProjectElementClient')

  return client
    .projectElementGetElementByProjectEntity(
      projectId,
      projectEntityId,
      convertFromBaseObject(elementType),
    )
    .then(convertToProjectElementInfo)
}

export const publishElementsApi = (
  projectId: ProjectId,
  elementId: ProjectElementId,
): Promise<void> => {
  const client = getClient('ProjectElementClient')
  return client.projectElementPublishElement(projectId, elementId)
}

export const publishElementsByEntityIdApi = (
  projectId: ProjectId,
  entityId: string,
  elementType: BaseObject,
): Promise<void> => {
  const client = getClient('ProjectElementClient')
  return client.projectElementPublishElementByEntityId(
    projectId,
    entityId,
    convertFromBaseObject(elementType),
  )
}

/// StateClient

export const setStateWindowElementApi = (
  projectId: ProjectId,
  projectElementId: string,
  tabId: TabId,
  state: TabElementStateType,
): Promise<TabElementInfo[]> => {
  const client = getClient('StateClient')
  return client
    .stateSetStateTabElements(projectId, projectElementId, { tabId, state })
    .then(tabInfos => tabInfos.map(convertToTabElementInfo))
}

export const setStateWindowElementByEntityIdApi = (
  projectId: ProjectId,
  entityId: string,
  entityType: BaseObject,
  tabId: string,
  state: TabElementStateType,
): Promise<TabElementInfo[]> => {
  const client = getClient('StateClient')
  const elementType = convertToApiElementType(entityType)
  return client
    .stateSetStateTabElementsByEntityId(projectId, entityId, { elementType, tabId, state })
    .then(tabInfos => tabInfos.map(convertToTabElementInfo))
}

/// WindowClient

export const getTabsByWorkAreaIdApi = (
  workAreaId: WorkAreaId,
  needIncludeHidden: NeedIncludeHidden = false,
): Promise<TabInfo[]> => {
  const client = getClient('TabClient')
  return client
    .tabGetTabsByWorkAreaId(workAreaId, { needIncludeHidden })
    .then(tabInfos => tabInfos.map(convertTabInfo))
}

export const setTabConfigApi = (
  projectId: ProjectId,
  tabId: TabId,
  domainType: DomainType,
): Promise<boolean> => {
  const client = getClient('TabClient')
  const query = { settingKey: 'domainType', settingValue: domainType }
  return client.tabEditSettingOfConfig(projectId, tabId, query)
}

export const deleteTabFromProjectApi = (tabId: TabId): Promise<boolean> => {
  const client = getClient('TabClient')
  return client.tabDeleteTab({ tabId })
}

export const addTabInWorkAreaApi = (
  workAreaId: WorkAreaId,
  name: string,
  tabType: TabType,
  orderNum: number,
): Promise<string> => {
  const client = getClient('TabClient')
  return client.tabAddTab({ name, orderNum, tabType, workAreaUid: workAreaId })
}

/// WorkAreaClient

export const getWorkAreasByProjectIdApi = (
  projectId: ProjectId,
  needIncludeHidden: NeedIncludeHidden = false,
): Promise<WorkArea[]> => {
  const client = getClient('WorkAreaClient')
  return client
    .workAreaGetWorkAreasByProjectId(projectId, { needIncludeHidden })
    .then(workAreas => workAreas.map(convertToWorkArea))
}

export const addWorkAreaApi = (projectId: ProjectId): Promise<string> => {
  const client = getClient('WorkAreaClient')
  return client.workAreaAddWorkArea({
    projectUid: projectId,
    name: 'name',
    orderNum: 0,
  })
}

export const deleteWorkAreaFromProjectApi = (workAreaId: WorkAreaId): Promise<boolean> => {
  const client = getClient('WorkAreaClient')
  return client.workAreaDeleteWorkArea({ workAreaId })
}

/// TabElementClient

export const getTabElementsByTabIdApi = async (tabId: TabId): Promise<TabElementInfo[]> => {
  const client = getClient('TabElementClient')
  return client
    .tabElementGetTabElementsByTabId(tabId)
    .then(tabInfos => tabInfos.map(convertToTabElementInfo))
}

/// DeleteElementClient

export const getElementFromTrashApi = (
  projectId: ProjectId,
  projectEntityId: ProjectElementId,
  elementType: BaseObject,
): Promise<ProjectElementInfo | undefined> => {
  const client = getClient('DeleteElementClient')

  return client
    .deleteElementGetElementDeletedToTrash(
      projectId,
      projectEntityId,
      convertFromBaseObject(elementType),
    )
    .then(from => (isDefined(from) ? convertToProjectElementInfo(from) : undefined))
}
